import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { bookTitleOf } from "../bookScreen";
import TypeBox from "../../widgets/TypeBox";
import { setSection, updateSection } from "../../../assignmentSlice";
import { calculateReadingAccuracy, useSpeechRecognition } from "../../../../../hooks/useSpeechRecognition";
import { BOOK_SCREEN_HEIGHT } from "../EbookPage";
import "../EbookPage.css";
import "./PhonicsStepPage.css";

// 파닉스북(Ginedu Phonics, 2026-10-19)의 입모양 · 이야기 듣기 · 듣기 말하기 문제 섹션. 책 넘겨 보기는 EbookPage 가 그린다.
// question 모양은 api/assignment/phonics_book.py 가 만든다(question.phonics_step 으로 갈린다).
//
// 저장은 리더스북과 같은 ebook_state 한 줄이다. 문제 결과는 results 에 {question_id: {score}} 로 남겨
// calculate_grade 가 리더스북 채점과 똑같이 센다. 입모양 · 이야기는 점수가 없고 다 보면(다 들으면) 완료다.
//
// - 소리 파일이 없는 문항(지인에듀가 아직 못 만든 것)은 tts_text 를 브라우저 TTS 로 읽는다(지인에듀 안내).
// - 지금 읽는 낱말은 글자 굵기와 색으로만 드러낸다. 배경을 칠하지 않는다(직독직해와 같은 규칙).

const STATE_TYPE = "ebook_state";
const STEP_MOUTH = "mouth";
const STEP_STORY = "story";
const STEP_QUIZ = "quiz";
const STEP_SPEAK = "speak";
// 따라 말하기 통과 점수(직독직해 PASSING_ACCURACY 와 같다). 받아쓰기가 관사 · 복수형을 자주 흘려 100 을 요구하지 않는다.
const SPEAK_PASSING_ACCURACY = 70;
// 따라 말하기 결과를 담는 자리. calculate_grade 가 ebook_state.speaking 의 first_accuracy 를 점수로 센다.
const SPEAK_STATE_KEY = "story";
const ACCENT = "#D6336C";
// 말하기 문항: 받아쓴 낱말 중 하나가 정답(또는 허용 답)과 같으면 맞다. 한 낱말이라 정확도 대신 일치로 본다.
const SPEAK_TYPE_PREFIX = "SPEAK";
const SPEAK_TRY_COUNT = 2;
const QUESTION_TEXT_BY_TYPE = {
  SOUND_WORD: "잘 듣고 알맞은 낱말을 고르세요.",
  SOUND_IPA: "잘 듣고 알맞은 소리 기호를 고르세요.",
  SOUND_SPELLING: "잘 듣고 알맞은 철자를 고르세요.",
  WORD_SOUND: "낱말을 바르게 읽은 소리를 고르세요.",
  IPA_WORD: "소리 기호에 맞는 낱말을 고르세요.",
  PSEUDOWORD_SOUND: "글자를 바르게 읽은 소리를 고르세요.",
  SOUND_PICTURE: "잘 듣고 알맞은 그림을 고르세요.",
  WORD_PICTURE: "낱말에 맞는 그림을 고르세요.",
  PICTURE_WORD: "그림에 맞는 낱말을 고르세요.",
  SOUND_MEANING: "잘 듣고 알맞은 뜻을 고르세요.",
  WORD_MEANING: "낱말의 뜻을 고르세요.",
  SENTENCE_PICTURE: "잘 듣고 알맞은 그림을 고르세요.",
  SENTENCE_MEANING: "잘 듣고 알맞은 뜻을 고르세요.",
  SENTENCE_RESULT: "잘 듣고 일어난 일을 고르세요.",
  QUESTION_RESPONSE: "잘 듣고 알맞은 대답을 고르세요.",
  DIALOGUE_COMPLETE: "대화를 듣고 빈칸에 알맞은 말을 고르세요.",
  SPEAK_WORD: "단어를 소리 내어 읽어 보세요.",
  SPEAK_IPA: "소리 기호를 보고 낱말을 읽어 보세요.",
};
// 소리를 듣고 푸는 문항(문제 소리가 없으면 tts_text 를 읽는다)
const LISTENING_TYPE_LIST = [
  "SOUND_WORD", "SOUND_IPA", "SOUND_SPELLING", "SOUND_PICTURE", "SOUND_MEANING",
  "SENTENCE_PICTURE", "SENTENCE_MEANING", "SENTENCE_RESULT", "QUESTION_RESPONSE", "DIALOGUE_COMPLETE",
];

// 그림 문항은 그림 파일이 모두 있을 때만 낸다(서버 phonics_book.PICTURE_ITEM_TYPE_LIST 와 같다). 예전에 출제된 과제에는
// 이모지와 설명만 실려 있어서 여기서도 거른다.
const PICTURE_ITEM_TYPE_LIST = ["SOUND_PICTURE", "WORD_PICTURE", "PICTURE_WORD", "SENTENCE_PICTURE"];
const hasEveryPicture = (item) =>
  item.question_type === "PICTURE_WORD"
    ? Boolean(item.prompt_picture?.picture_url)
    : (item.options || []).every((option) => option.picture_url);
const isShownQuizItem = (item) => !PICTURE_ITEM_TYPE_LIST.includes(item.question_type) || hasEveryPicture(item);

const isNarrowScreen = () => typeof window !== "undefined" && Math.min(window.innerWidth, window.screen.width) <= 768;

const readSavedState = (answerList) =>
  (Array.isArray(answerList) ? answerList : []).find((one) => one && one.type === STATE_TYPE) || {};

// 소리 하나만 울린다. 새로 틀면 앞의 것은 멈춘다.
let currentAudio = null;
const stopSound = () => {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio = null;
  }
  if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel();
};
const playSound = (url, ttsText) =>
  new Promise((resolve) => {
    stopSound();
    if (url) {
      const audio = new Audio(url);
      currentAudio = audio;
      audio.onended = () => resolve(true);
      audio.onerror = () => resolve(false);
      audio.play().catch(() => resolve(false));
      return;
    }
    if (ttsText && typeof window !== "undefined" && window.speechSynthesis) {
      const utterance = new SpeechSynthesisUtterance(ttsText);
      utterance.lang = "en-US";
      utterance.rate = 0.85;
      utterance.onend = () => resolve(true);
      utterance.onerror = () => resolve(false);
      window.speechSynthesis.speak(utterance);
      return;
    }
    resolve(false);
  });

const normalizeWord = (text) => String(text || "").toLowerCase().replace(/[^a-z']/g, "");

const PhonicsStepPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { assignment, section } = useSelector((state) => state.assignment);
  const question = section?.question || {};
  const step = question.phonics_step;
  const accent = (question.book || {}).theme_color || ACCENT;
  const [isMobile, setIsMobile] = useState(isNarrowScreen);
  const savedRef = useRef({});

  useEffect(() => {
    const handleResize = () => setIsMobile(isNarrowScreen());
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      stopSound();
    };
  }, []);

  // section 이 아니라 section.id 에 건다. 저장할 때마다 section 이 바뀌어 다시 돌면 보던 자리에서 튄다.
  const savedState = useMemo(() => {
    const state = isPreview ? {} : readSavedState(section?.answer);
    savedRef.current = state;
    return state;
  }, [section?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = (patch, isFinished = false) => {
    if (isPreview || isReview) return;
    const nextState = { ...savedRef.current, ...patch, type: STATE_TYPE };
    savedRef.current = nextState;
    const answer = [nextState];
    dispatch(setSection({ ...section, answer }));
    dispatch(
      updateSection({
        sectionId: section.id,
        data: isFinished ? { answer, is_finished: true } : { answer },
        isSilent: !isFinished,
      })
    );
  };

  const leave = () => {
    stopSound();
    setCurrentStep(2);
  };

  return (
    <div className="ebook phonics" style={{ height: BOOK_SCREEN_HEIGHT, "--ebook-accent": accent, "--phonics-accent": accent }}>
      <div className="ebook__bar">
        <div className="ebook__heading">
          <TypeBox type={assignment?.type || "readers_book"} isMobile={isMobile} title={assignment?.title} />
          <div className="ebook__title">{bookTitleOf(assignment?.title || (question.book || {}).title)}</div>
          {!isMobile && section?.type_name && <div className="ebook__subtitle">{section.type_name}</div>}
        </div>
        <div className="ebook__audio-list">
          <button type="button" className="ebook__exit" onClick={leave}>
            나가기
          </button>
        </div>
      </div>
      <div className="phonics__body">
        {step === STEP_MOUTH && (
          <MouthStep question={question} savedState={savedState} isLocked={isPreview || isReview} save={save} onFinish={leave} />
        )}
        {step === STEP_STORY && (
          <StoryStep question={question} savedState={savedState} isMobile={isMobile} save={save} onFinish={leave} />
        )}
        {step === STEP_SPEAK && (
          <SpeakStep question={question} savedState={savedState} isLocked={isReview} isPreview={isPreview} save={save} onFinish={leave} />
        )}
        {step === STEP_QUIZ && (
          <QuizStep question={question} savedState={savedState} isLocked={isReview} save={save} onFinish={leave} />
        )}
      </div>
    </div>
  );
};

// ① 입모양: 소리마다 영상(낱소리 두 번 + 대표 낱말)을 끝까지 본다. 다 보면 완료다.
const MouthStep = ({ question, savedState, isLocked, save, onFinish }) => {
  const clipList = question.clips || [];
  const [clipIndex, setClipIndex] = useState(0);
  const [watchedList, setWatchedList] = useState(() => savedState.watched_clips || []);
  const videoRef = useRef(null);
  const clip = clipList[clipIndex] || {};
  const isAllWatched = isLocked || clipList.every((_, index) => watchedList.includes(index));

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.play().catch(() => {});
  }, [clipIndex]);

  const handleEnded = () => {
    if (watchedList.includes(clipIndex)) return;
    const nextWatchedList = [...watchedList, clipIndex].sort((a, b) => a - b);
    setWatchedList(nextWatchedList);
    const isDone = clipList.every((_, index) => nextWatchedList.includes(index));
    save({ watched_clips: nextWatchedList }, isDone);
  };

  return (
    <div className="phonics-mouth">
      <div className="phonics-mouth__tabs">
        {clipList.map((one, index) => (
          <button
            type="button"
            key={index}
            className={`phonics-mouth__tab${index === clipIndex ? " is-now" : ""}${watchedList.includes(index) ? " is-done" : ""}`}
            onClick={() => setClipIndex(index)}
          >
            <b>{one.grapheme}</b>
            <span>{one.ipa}</span>
          </button>
        ))}
      </div>
      <div className="phonics-mouth__stage">
        <div className="phonics-mouth__video">
          <video ref={videoRef} key={clip.video_url} src={clip.video_url} playsInline controls preload="auto" onEnded={handleEnded} />
        </div>
        <div className="phonics-mouth__card">
          <div className="phonics-mouth__letter">{clip.grapheme}</div>
          <div className="phonics-mouth__ipa">{clip.ipa}</div>
          <div className="phonics-mouth__sounds">
            <button type="button" className="phonics__sound" onClick={() => playSound(clip.sound_url)} disabled={!clip.sound_url}>
              <SpeakerIcon /> 소리 {clip.ipa}
            </button>
            <button type="button" className="phonics__sound" onClick={() => playSound(clip.key_word_audio_url, clip.key_word)}>
              <SpeakerIcon /> {clip.key_word}
            </button>
          </div>
          {clip.key_word_ipa && <div className="phonics-mouth__keyword-ipa">{clip.key_word_ipa}</div>}
        </div>
      </div>
      <div className="phonics__footer">
        <span className="phonics__count">
          {watchedList.length} / {clipList.length} 소리 봤어요
        </span>
        {clipIndex < clipList.length - 1 && (
          <button type="button" className="phonics__next" onClick={() => setClipIndex(clipIndex + 1)}>
            다음 소리
          </button>
        )}
        <button type="button" className="phonics__finish" onClick={onFinish} disabled={!isAllWatched}>
          {isAllWatched ? "다 봤어요" : "영상을 끝까지 봐요"}
        </button>
      </div>
    </div>
  );
};

// ② 이야기 듣기: 배역 낭독에 맞춰 지금 읽는 낱말을 진하게 한다. 줄을 누르면 그 줄부터, 낱말을 누르면 그 낱말 소리만.
const StoryStep = ({ question, savedState, isMobile, save, onFinish }) => {
  const lineList = question.lines || [];
  const sceneList = question.scenes || [];
  const audioRef = useRef(null);
  const lineRefs = useRef({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [nowMs, setNowMs] = useState(-1);
  const [isHeard, setIsHeard] = useState(Boolean(savedState.story_done));
  const hasTiming = Boolean(question.audio_url) && lineList.some((line) => typeof line.start_ms === "number");

  const readingLineIndex = hasTiming
    ? lineList.findIndex((line) => line.start_ms <= nowMs && nowMs < line.end_ms)
    : -1;
  // 큰 그림은 한두 장이라 이야기를 그 수만큼 고르게 나눠 보인다
  const sceneIndex = sceneList.length
    ? Math.min(sceneList.length - 1, Math.floor((Math.max(readingLineIndex, 0) / Math.max(lineList.length, 1)) * sceneList.length))
    : -1;

  useEffect(() => {
    const element = lineRefs.current[readingLineIndex];
    if (element) element.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [readingLineIndex]);

  const markHeard = () => {
    if (isHeard) return;
    setIsHeard(true);
    save({ story_done: true }, true);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      stopSound();
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  };

  const playLine = (line) => {
    const audio = audioRef.current;
    if (!audio || typeof line.start_ms !== "number") return;
    stopSound();
    audio.currentTime = line.start_ms / 1000;
    audio.play().catch(() => setIsPlaying(false));
  };

  const playWord = (event, text) => {
    event.stopPropagation();
    const url = (question.word_audio || {})[normalizeWord(text)];
    if (audioRef.current) audioRef.current.pause();
    playSound(url, text);
  };

  const renderLineText = (line) => {
    const wordList = line.words || [];
    if (!wordList.length) {
      return line.text.split(/(\s+)/).map((token, index) =>
        token.trim() ? (
          <span key={index} className="phonics-story__word" onClick={(event) => playWord(event, token)}>{token}</span>
        ) : token
      );
    }
    // 대본 글을 낱말 시간표 차례대로 찾아가며 쪼갠다. 문장 부호는 앞 낱말에 붙여 둔다.
    const pieceList = [];
    let cursor = 0;
    wordList.forEach((word, index) => {
      const at = line.text.indexOf(word.text, cursor);
      if (at < 0) return;
      if (at > cursor) pieceList.push({ text: line.text.slice(cursor, at) });
      pieceList.push({ text: word.text, word, key: index });
      cursor = at + word.text.length;
    });
    if (cursor < line.text.length) pieceList.push({ text: line.text.slice(cursor) });
    return pieceList.map((piece, index) =>
      piece.word ? (
        <span
          key={index}
          className={`phonics-story__word${piece.word.start_ms <= nowMs && nowMs < piece.word.end_ms ? " is-reading" : ""}`}
          onClick={(event) => playWord(event, piece.word.text)}
        >
          {piece.text}
        </span>
      ) : (
        <span key={index}>{piece.text}</span>
      )
    );
  };

  return (
    <div className={`phonics-story${isMobile ? " is-narrow" : ""}`}>
      {question.audio_url && (
        <audio
          ref={audioRef}
          src={question.audio_url}
          preload="auto"
          onTimeUpdate={() => setNowMs(audioRef.current.currentTime * 1000)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => {
            setIsPlaying(false);
            setNowMs(-1);
            markHeard();
          }}
        />
      )}
      {sceneList.length > 0 && (
        <div className="phonics-story__picture">
          <img src={sceneList[Math.max(sceneIndex, 0)].picture_url} alt="" />
        </div>
      )}
      <div className="phonics-story__script">
        <h3>{question.title}</h3>
        {lineList.map((line, index) => {
          const picture = (question.speaker_pictures || {})[line.speaker];
          const isNarrator = !picture;
          return (
            <div
              key={line.idx ?? index}
              ref={(element) => {
                lineRefs.current[index] = element;
              }}
              className={`phonics-story__line${index === readingLineIndex ? " is-reading" : ""}${isNarrator ? " is-narrator" : ""}`}
              onClick={() => playLine(line)}
            >
              {picture ? <img className="phonics-story__face" src={picture} alt="" /> : <span className="phonics-story__face" />}
              <div>
                {!isNarrator && <div className="phonics-story__speaker">{line.speaker}</div>}
                <div className="phonics-story__text">{renderLineText(line)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="phonics__footer">
        {question.audio_url && (
          <button type="button" className="phonics__play" onClick={togglePlay} aria-label={isPlaying ? "멈춤" : "듣기"}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
        )}
        <span className="phonics__count">낱말을 누르면 그 낱말만 들려줘요</span>
        <button
          type="button"
          className="phonics__finish"
          onClick={() => {
            if (!question.audio_url) markHeard();
            onFinish();
          }}
          disabled={Boolean(question.audio_url) && !isHeard}
        >
          {isHeard || !question.audio_url ? "다 들었어요" : "끝까지 들어요"}
        </button>
      </div>
    </div>
  );
};

// ③ 이야기 따라 말하기: 배역 목소리로 한 줄을 듣고, 아이가 마이크를 눌러 읽는다. 첫 시도 정확도가 점수다(직독직해와 같다).
const SpeakStep = ({ question, savedState, isLocked, isPreview, save, onFinish }) => {
  const lineList = question.lines || [];
  const { isSupported, listen } = useSpeechRecognition();
  const audioRef = useRef(null);
  const stopTimerRef = useRef(null);
  const [spokenByLine, setSpokenByLine] = useState(() => (savedState.speaking || {})[SPEAK_STATE_KEY] || {});
  const firstOpenIndex = lineList.findIndex((line) => !spokenByLine[line.idx]);
  const [lineIndex, setLineIndex] = useState(firstOpenIndex < 0 ? 0 : firstOpenIndex);
  const [isListening, setIsListening] = useState(false);
  const line = lineList[lineIndex] || {};
  const spoken = spokenByLine[line.idx];
  const picture = (question.speaker_pictures || {})[line.speaker];
  const doneCount = lineList.filter((one) => spokenByLine[one.idx]).length;
  // 미리보기는 저장하지 않으니 읽지 않아도 넘어가게 둔다
  const isAllDone = isLocked || isPreview || doneCount === lineList.length;

  useEffect(() => () => clearTimeout(stopTimerRef.current), []);

  // 그 줄만 들려준다: 낭독 파일에서 줄 시작으로 옮겨 틀고 끝에서 멈춘다
  const playLine = (target = line) => {
    const audio = audioRef.current;
    if (!audio || typeof target.start_ms !== "number") return;
    stopSound();
    clearTimeout(stopTimerRef.current);
    audio.currentTime = target.start_ms / 1000;
    audio.play().catch(() => {});
    stopTimerRef.current = setTimeout(() => audio.pause(), Math.max(target.end_ms - target.start_ms, 300) + 150);
  };

  useEffect(() => {
    if (!isAllDone || !spoken) playLine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineIndex]);

  const record = async () => {
    if (audioRef.current) audioRef.current.pause();
    setIsListening(true);
    const { transcript } = await listen({ language: "en-US", maximumMilliseconds: Math.max(5000, (line.end_ms - line.start_ms) * 2.5) });
    setIsListening(false);
    if (!transcript) return;
    const accuracy = calculateReadingAccuracy(line.text, transcript);
    const previous = spokenByLine[line.idx];
    const next = {
      ...spokenByLine,
      [line.idx]: {
        first_accuracy: previous ? previous.first_accuracy : accuracy,
        last_accuracy: accuracy,
        spoken: transcript.slice(0, 300),
        attempt_count: (previous ? previous.attempt_count : 0) + 1,
      },
    };
    setSpokenByLine(next);
    const isDone = lineList.every((one) => next[one.idx]);
    save({ speaking: { ...(savedState.speaking || {}), [SPEAK_STATE_KEY]: next } }, isDone);
  };

  // 받아쓰기가 안 되는 단말(카카오톡 안 브라우저 등)은 듣고 넘어가게 둔다. 점수에는 넣지 않는다.
  const skip = () => {
    const next = { ...spokenByLine, [line.idx]: spokenByLine[line.idx] || { skipped: true } };
    setSpokenByLine(next);
    save({ speaking: { ...(savedState.speaking || {}), [SPEAK_STATE_KEY]: next } }, lineList.every((one) => next[one.idx]));
    if (lineIndex < lineList.length - 1) setLineIndex(lineIndex + 1);
  };

  const accuracy = spoken && typeof spoken.last_accuracy === "number" ? spoken.last_accuracy : null;
  return (
    <div className="phonics-speak">
      {question.audio_url && <audio ref={audioRef} src={question.audio_url} preload="auto" />}
      <div className="phonics-quiz__progress">
        <span className="phonics-quiz__block">따라 말하기</span>
        <span>
          {lineIndex + 1} / {lineList.length}
        </span>
        <div className="phonics-quiz__bar">
          <div style={{ width: `${(doneCount / Math.max(lineList.length, 1)) * 100}%` }} />
        </div>
      </div>
      <div className="phonics-speak__card">
        {picture ? <img className="phonics-speak__face" src={picture} alt="" /> : null}
        {line.speaker && line.speaker !== "Narrator" && <div className="phonics-speak__speaker">{line.speaker}</div>}
        <div className="phonics-speak__text">{line.text}</div>
        <div className="phonics-speak__actions">
          <button type="button" className="phonics__sound" onClick={() => playLine()}>
            <SpeakerIcon /> 듣기
          </button>
          {isSupported && !isLocked && (
            <button type="button" className={`phonics-quiz__mic${isListening ? " is-on" : ""}`} onClick={record} disabled={isListening}>
              <MicIcon /> {isListening ? "듣는 중…" : spoken ? "다시 읽기" : "누르고 읽기"}
            </button>
          )}
        </div>
        {accuracy !== null && (
          <div className={`phonics-speak__result${accuracy >= SPEAK_PASSING_ACCURACY ? " is-right" : " is-wrong"}`}>
            {/* 받아쓴 말은 보이지 않는다. 받아쓰기가 흘린 말을 아이가 제 잘못으로 읽는다(직독직해와 같다) */}
            {accuracy >= SPEAK_PASSING_ACCURACY ? "잘 읽었어요!" : "한 번 더 읽어 볼까요?"} <b>{accuracy}점</b>
          </div>
        )}
      </div>
      <div className="phonics__footer">
        {lineIndex > 0 && (
          <button type="button" className="phonics__next" onClick={() => setLineIndex(lineIndex - 1)}>
            이전 줄
          </button>
        )}
        {!isLocked && !spoken && (
          <button type="button" className="phonics-quiz__skip" onClick={skip}>
            건너뛰기
          </button>
        )}
        {lineIndex < lineList.length - 1 ? (
          <button type="button" className="phonics__finish" onClick={() => setLineIndex(lineIndex + 1)} disabled={!spoken && !isLocked && !isPreview}>
            다음 줄
          </button>
        ) : (
          <button type="button" className="phonics__finish" onClick={onFinish} disabled={!isAllDone}>
            다 읽었어요
          </button>
        )}
      </div>
    </div>
  );
};

// ④ 듣기 · 말하기 문제: 한 문항씩 푼다. 고르면 바로 맞았는지 보여 주고 다음으로 간다. 다 풀면 점수를 낸다.
const QuizStep = ({ question, savedState, isLocked, save, onFinish }) => {
  const itemList = (question.items || []).filter(isShownQuizItem);
  const [results, setResults] = useState(() => savedState.results || {});
  const firstOpenIndex = itemList.findIndex((item) => !(results || {})[item.question_id]);
  const [itemIndex, setItemIndex] = useState(firstOpenIndex < 0 ? 0 : firstOpenIndex);
  const [isSummary, setIsSummary] = useState(firstOpenIndex < 0 && itemList.length > 0);
  const item = itemList[itemIndex] || {};
  const result = results[item.question_id];

  const recordResult = (score, picked) => {
    const nextResults = { ...results, [item.question_id]: { score, picked } };
    setResults(nextResults);
    const isDone = itemList.every((one) => nextResults[one.question_id]);
    save({ results: nextResults, last_item: itemIndex }, isDone);
  };

  const goNext = () => {
    stopSound();
    if (itemIndex >= itemList.length - 1) setIsSummary(true);
    else setItemIndex(itemIndex + 1);
  };

  if (isSummary) {
    const total = itemList.reduce((sum, one) => sum + Number((results[one.question_id] || {}).score || 0), 0);
    const blockList = Array.from(new Set(itemList.map((one) => one.block)));
    return (
      <div className="phonics-quiz phonics-quiz--summary">
        <div className="phonics-quiz__score">
          <span>맞힌 문제</span>
          <b>
            {Math.round(total)} / {itemList.length}
          </b>
        </div>
        <div className="phonics-quiz__blocks">
          {blockList.map((block) => {
            const blockItemList = itemList.filter((one) => one.block === block);
            const right = blockItemList.filter((one) => Number((results[one.question_id] || {}).score) >= 1).length;
            return (
              <div key={block}>
                <span>{block}</span>
                <b>
                  {right} / {blockItemList.length}
                </b>
              </div>
            );
          })}
        </div>
        <div className="phonics__footer">
          <button
            type="button"
            className="phonics__next"
            onClick={() => {
              setIsSummary(false);
              setItemIndex(0);
            }}
          >
            문제 다시 보기
          </button>
          <button type="button" className="phonics__finish" onClick={onFinish}>
            끝내기
          </button>
        </div>
      </div>
    );
  }

  const isSpeak = String(item.question_type || "").startsWith(SPEAK_TYPE_PREFIX);
  return (
    <div className="phonics-quiz">
      <div className="phonics-quiz__progress">
        <span className="phonics-quiz__block">{item.block}</span>
        <span>
          {itemIndex + 1} / {itemList.length}
        </span>
        <div className="phonics-quiz__bar">
          <div style={{ width: `${((itemIndex + 1) / Math.max(itemList.length, 1)) * 100}%` }} />
        </div>
      </div>
      <div className="phonics-quiz__ask">{QUESTION_TEXT_BY_TYPE[item.question_type] || "알맞은 것을 고르세요."}</div>
      <QuizPrompt item={item} isAnswered={Boolean(result)} />
      {isSpeak ? (
        <SpeakAnswer key={item.question_id} item={item} result={result} isLocked={isLocked} onResult={recordResult} />
      ) : (
        <ChoiceAnswer key={item.question_id} item={item} result={result} isLocked={isLocked} onResult={recordResult} />
      )}
      <div className="phonics__footer">
        {itemIndex > 0 && (
          <button type="button" className="phonics__next" onClick={() => setItemIndex(itemIndex - 1)}>
            이전
          </button>
        )}
        <button type="button" className="phonics__finish" onClick={goNext} disabled={!result && !isLocked}>
          {itemIndex >= itemList.length - 1 ? "결과 보기" : "다음"}
        </button>
      </div>
    </div>
  );
};

const QuizPrompt = ({ item, isAnswered }) => {
  const isListening = LISTENING_TYPE_LIST.includes(item.question_type);
  const isSpeakWord = item.question_type === "SPEAK_WORD";
  // 문제를 열면 소리 문항은 한 번 들려준다
  useEffect(() => {
    if (isListening) playSound(item.prompt_audio_url, item.tts_text);
    return stopSound;
  }, [item.question_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dialogueLineList = item.question_type === "DIALOGUE_COMPLETE" ? String(item.tts_text || "").split(" / ") : [];
  return (
    <div className="phonics-quiz__prompt">
      {isListening && (
        <button type="button" className="phonics-quiz__listen" onClick={() => playSound(item.prompt_audio_url, item.tts_text.replace(/___/g, ""))}>
          <SpeakerIcon size={28} />
          다시 듣기
        </button>
      )}
      {dialogueLineList.length > 1 && (
        <div className="phonics-quiz__dialogue">
          {dialogueLineList.map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </div>
      )}
      {item.prompt_text && <div className="phonics-quiz__word">{item.prompt_text}</div>}
      {item.prompt_picture?.picture_url && (
        <div className="phonics-quiz__picture">
          <img src={item.prompt_picture.picture_url} alt="" />
        </div>
      )}
      {/* 말하기는 듣지 않고 스스로 읽는 문항이다(레벨테스트와 같다). 본보기 소리는 읽고 난 뒤에만 들려준다 */}
      {isSpeakWord && isAnswered && (
        <button type="button" className="phonics__sound" onClick={() => playSound(item.model_audio_url, item.tts_text)}>
          <SpeakerIcon /> 들어 보기
        </button>
      )}
    </div>
  );
};

// 보기를 누르면 바로 고른 것이다. 소리 보기는 네모 안 스피커로 먼저 들어 보고, 네모를 눌러 고른다
// (2026-10-20 대표: 번호 단추 없이 네모를 바로. 듣기와 고르기가 한 단추면 다 들어 보기 전에 골라져서 스피커를 따로 둔다).
const ChoiceAnswer = ({ item, result, isLocked, onResult }) => {
  const optionList = item.options || [];
  const isAudioOption = optionList.some((option) => option.tts_text && !option.text);
  const isPictureOption = optionList.some((option) => option.picture_url);
  const picked = result ? result.picked : undefined;
  return (
    <div className={`phonics-quiz__options${isPictureOption ? " is-picture" : ""}${isAudioOption ? " is-audio" : ""}`}>
      {optionList.map((option, index) => {
        const isPicked = picked === index;
        const stateClass = result ? (option.is_correct ? " is-right" : isPicked ? " is-wrong" : "") : "";
        const pick = () => {
          if (result || isLocked) return;
          onResult(option.is_correct ? 1 : 0, index);
        };
        if (isAudioOption) {
          return (
            <div
              key={index}
              role="button"
              tabIndex={0}
              className={`phonics-quiz__option phonics-quiz__option--audio${stateClass}`}
              onClick={pick}
              onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && pick()}
            >
              <button
                type="button"
                className="phonics-quiz__option-speaker"
                aria-label={`${index + 1}번 소리 듣기`}
                onClick={(event) => {
                  event.stopPropagation();
                  playSound(option.audio_url, option.tts_text);
                }}
              >
                <SpeakerIcon size={22} />
              </button>
              <span className="phonics-quiz__option-number">{index + 1}번</span>
            </div>
          );
        }
        return (
          <button type="button" key={index} className={`phonics-quiz__option${stateClass}`} onClick={pick}>
            {option.picture_url && <img className="phonics-quiz__option-picture" src={option.picture_url} alt="" />}
            {!option.picture_url && option.text && <span>{option.text}</span>}
          </button>
        );
      })}
    </div>
  );
};

const SpeakAnswer = ({ item, result, isLocked, onResult }) => {
  const { isSupported, listen } = useSpeechRecognition();
  const [isListening, setIsListening] = useState(false);
  const [heard, setHeard] = useState(result ? result.picked || "" : "");
  const [failCount, setFailCount] = useState(0);
  const acceptedList = [...(item.accepted_alternatives || []), ...(item.options || []).filter((one) => one.is_correct).map((one) => one.text)]
    .map(normalizeWord)
    .filter(Boolean);

  const handleListen = async () => {
    stopSound();
    setIsListening(true);
    const { transcript } = await listen({ language: "en-US", maximumMilliseconds: 5000 });
    setIsListening(false);
    setHeard(transcript || "");
    const isRight = String(transcript || "").split(/\s+/).map(normalizeWord).some((word) => acceptedList.includes(word));
    if (isRight) {
      onResult(1, transcript);
      return;
    }
    // 못 알아들었으면(빈 받아쓰기) 기회를 쓰지 않는다. 틀리면 한 번 더 해 보고, 그래도 틀리면 정답을 보여 준다.
    if (!transcript) return;
    const nextFailCount = failCount + 1;
    setFailCount(nextFailCount);
    if (nextFailCount >= SPEAK_TRY_COUNT) onResult(0, transcript);
  };

  const answerWord = (item.options || []).find((one) => one.is_correct)?.text || "";
  return (
    <div className="phonics-quiz__speak">
      {result && (
        <div className={`phonics-quiz__speak-result${Number(result.score) >= 1 ? " is-right" : " is-wrong"}`}>
          {Number(result.score) >= 1 ? "잘 읽었어요!" : `정답은 ${answerWord}`}
        </div>
      )}
      {!result && !isLocked && (
        <>
          {isSupported && (
            <button type="button" className={`phonics-quiz__mic${isListening ? " is-on" : ""}`} onClick={handleListen} disabled={isListening}>
              <MicIcon /> {isListening ? "듣는 중…" : "누르고 말하기"}
            </button>
          )}
          {!isListening && heard && <small className="phonics-quiz__heard">다시 해 볼까요?</small>}
          <button type="button" className="phonics-quiz__skip" onClick={() => onResult(0, "")}>
            건너뛰기
          </button>
        </>
      )}
    </div>
  );
};

const SpeakerIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
    <path d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
  </svg>
);
const MicIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
    <path d="M6 11a6 6 0 0 0 12 0M12 17v4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
  </svg>
);
const PlayIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 2.5v11l9-5.5z" fill="currentColor" /></svg>
);
const PauseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true"><rect x="3" y="2" width="3.5" height="12" rx="1" fill="currentColor" /><rect x="9.5" y="2" width="3.5" height="12" rx="1" fill="currentColor" /></svg>
);

export default PhonicsStepPage;
